/** The width of the element a chart is drawn into, kept current.
 *
 * `@visx/responsive` is not installed (ADR-0090 chose five packages), and a chart that is a fixed
 * pixel width is a chart that overflows at 400px. A `ResizeObserver` on the container is the whole
 * of what is needed. jsdom has none, so the fallback width is what a test sees, which is also what
 * the first paint uses before the observer has reported.
 */

import type { RefObject } from "react";
import { useEffect, useState } from "react";

export function useChartWidth(ref: RefObject<HTMLElement | null>, fallback = 320): number {
  const [width, setWidth] = useState(fallback);
  useEffect(() => {
    const node = ref.current;
    if (!node || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver((entries) => {
      const next = Math.floor(entries[0]?.contentRect.width ?? 0);
      if (next > 0) setWidth(next);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [ref]);
  return width;
}
